import { message } from 'antd';
import { getBaseUrl } from 'shared/utils/sse';
import {
  CallTask,
  CallTaskStatus,
  CompletedCallTaskStates,
  DialTask,
  InProgressCallTaskStates,
  OutboundAMDState,
  SourceType,
} from 'types';
import { OutboundTwilio } from './Twilio';
import {
  getDialTask,
  postCloseDialSession,
  postCloseDialTask,
  postDialSession,
  postDialTask,
  postOutboundHandshakeReply,
  postRetryDialTask,
  postMarkDialTaskNumberInvalid,
  deleteLbbContactPhoneNumber,
} from 'shared/api';
import { EventEmitter } from 'events';
import { makeLog } from 'shared/utils/log';
import { SalesfloorRoom } from 'types/salesfloor';
import { transcribe } from './transcribe';
import { cloneDeep } from 'lodash';
import { getTaskPendingNumbers } from 'shared/utils/outbound';

const log = makeLog('OutboundSession');

const SSE_ENDPOINT = `${getBaseUrl()}/api/v1/outbound/dial_session/stream`;

interface LoadMoreDataResult {
  cursor: string | null;
  tasks: CallTask[];
  next: (() => Promise<LoadMoreDataResult>) | null;
}

interface OutboundSessionStartParams {
  preRecordedVoicemailId?: number | null;
  amdState: OutboundAMDState;
  inputDevice: string;
  loadMoreData?: () => Promise<LoadMoreDataResult>;
  muted: boolean;
  parallelCallCount: number;
  selectedNumber: string;
  sourceId: number | null;
  sourceType: SourceType;
  stopOnCallCompleted: boolean;
  taskQueue: CallTask[];
}

export enum OutboundSessionStates {
  acceptingCall = 'ACCEPTING_CALL',
  awaitingUpdates = 'AWAITING_UPDATES',
  closed = 'CLOSED',
  connected = 'CALL_CONNECTED',
  dialing = 'DIALING',
  initializing = 'INITIALIZING',
  loading = 'LOADING',
  paused = 'PAUSED',
  reconnecting = 'RECONNECTING',
}

// Invariants:
// * When isOpen(), sessionId and co. are non-null.
export class OutboundSession extends EventEmitter {
  acceptCallId: null | string;
  acceptDeadline: null | number;
  amdMode: 'OFF' | 'FAST' | 'SLOW';
  awaitDeadline: null | number;
  callStartedAt: null | Date;
  cookie = 0;
  eventSource: EventSource | null;
  lastStartTime = 0;
  liveListenRoom: null | SalesfloorRoom;
  loadMoreData: (() => Promise<LoadMoreDataResult>) | null;
  parallelCallCount: number;
  queueTick: number;
  selectedNumber: null | string;
  sessionId: number | null;
  sourceId: number | null;
  sourceType: SourceType | null;
  status: OutboundSessionStates;
  stopOnCallCompleted: boolean;
  taskQueue: CallTask[];
  timer: NodeJS.Timeout | null;
  twilioInstance: OutboundTwilio | null;
  failedTaskCounts: Record<string, number>;

  constructor() {
    super();
    this.acceptCallId = null;
    this.acceptDeadline = null;
    this.amdMode = 'OFF';
    this.awaitDeadline = null;
    this.callStartedAt = null;
    this.eventSource = null;
    this.liveListenRoom = null;
    this.loadMoreData = null;
    this.parallelCallCount = 1;
    this.queueTick = 1000;
    this.selectedNumber = null;
    this.sessionId = null;
    this.sourceId = null;
    this.sourceType = null;
    this.status = OutboundSessionStates.closed;
    this.stopOnCallCompleted = false;
    this.taskQueue = [];
    this.timer = null;
    this.twilioInstance = null;
    this.failedTaskCounts = {};
  }

  async start({
    preRecordedVoicemailId,
    amdState,
    inputDevice,
    loadMoreData = async () => ({ cursor: null, tasks: [], next: null }),
    muted,
    parallelCallCount,
    selectedNumber,
    sourceId,
    sourceType,
    stopOnCallCompleted = false,
    taskQueue,
  }: OutboundSessionStartParams) {
    // use-outbound maintains a single OutbountSession across an agent's
    // entire browser session, and some of the buttons that control
    // when the session is started or stopped don't have a good way
    // to prevent multi-clicks (beyond debouncing). So, to avoid ever
    // running two overlapping start() calls, we issue a cookie to
    // every method call. If after a scheduling point (i.e. an await or
    // similar), the cookie has changed, then we bail.
    const now = new Date().getTime();
    const delta = now - this.lastStartTime;
    this.lastStartTime = now;
    if (delta < 1000) {
      log.warn('Session start() was debounced.');
      return;
    }

    const cookie = ++this.cookie;
    const checkCookie = () => {
      if (cookie !== this.cookie) {
        log.error('Session has overlapping start() calls.', {
          cookie,
          sessionCookie: this.cookie,
        });
        throw new Error('Session has overlapping start() calls.');
      }
    };

    if (this.status !== OutboundSessionStates.closed) {
      log.warn('Session is already open. Closing.', { status: this.status });
      await this.stop(true);
      checkCookie();
    }

    log.debug('Starting...', {
      parallelCallCount,
      selectedNumber,
      sourceId,
      sourceType,
      stopOnCallCompleted,
      taskQueue,
    });
    this.loadMoreData = loadMoreData;
    this.parallelCallCount = parallelCallCount;
    this.selectedNumber = selectedNumber;
    this.setMachineDetection(amdState, preRecordedVoicemailId);
    this.sourceId = sourceId;
    this.sourceType = sourceType;
    this.stopOnCallCompleted = stopOnCallCompleted;
    this.taskQueue = taskQueue;
    this.twilioInstance = new OutboundTwilio();

    this.setStatus(OutboundSessionStates.initializing);

    const { data } = await postDialSession({
      selectedNumber,
      sourceType,
      startingTaskCount: this.taskQueue.length,
      sourceId,
      preRecordedVoicemailId,
    }).catch(() => {
      this.fail('Failed to start dial session');
      return { data: null };
    });

    if (!data) return;

    checkCookie();

    this.liveListenRoom = data.liveListenRoom || null;
    this.sessionId = data.id;
    this.emit('session.created', data);
    checkCookie();

    log.debug('Session started. Setting up SSE and Twilio...', data);
    await this.setupSSE(data.id);
    checkCookie();
    await this.setupTwilio(data.id, inputDevice, muted);
    checkCookie();
    this.setStatus(OutboundSessionStates.dialing);
    this.startTimer();
  }

  async hangUp({
    except,
    canRetry,
    wasAnswered,
  }: {
    except?: string[];
    canRetry?: boolean;
    wasAnswered?: boolean;
  } = {}): Promise<boolean> {
    log.assert(this.isOpen(), 'hangUp() called on closed session');
    const inProgressTasks = this.inProgressTasks();
    log.debug('Closing all in-progress DialTasks.', {
      except,
      canRetry,
      wasAnswered,
      inProgressTasks,
    });
    const promises = [];
    for (const task of inProgressTasks) {
      if (except?.includes(task.frontendId)) {
        continue;
      }
      promises.push(this.hangOneUp(task as CallTask, canRetry, wasAnswered));
    }
    try {
      for (const ok of await Promise.all(promises)) {
        if (!ok) {
          return false;
        }
      }
      return true;
    } catch (error) {
      log.error('Failed to hang up some tasks.', { error: formatError(error) });
      return false;
    }
  }

  async skip() {
    log.debug('Skipping any in-progress DialTasks.');
    if (!this.isOpen()) {
      log.error('skip() called on closed session.', { status: this.status });
      return;
    }
    // XXX: Avoid marking skipped calls as answered. A skipped call in a
    // sequence should progress to the next step (i.e. it should log as
    // "unanswered" in the CRM).
    const hungUp = await this.hangUp({ canRetry: true });
    if (!hungUp) {
      log.warn('Failed to hang up.', { status: this.status });
      message.warn('Failed to skip call. Please try again.');
    } else if (this.status === OutboundSessionStates.paused) {
      log.debug('Calls skipped. Resuming...');
      await this.resume();
    }
  }

  async pause() {
    log.debug('Pausing session.');
    if (!this.isOpen()) {
      log.error('pause() called on closed session.', { status: this.status });
      return;
    }
    const status = this.status;
    this.setStatus(OutboundSessionStates.paused);
    const hungUp = await this.hangUp({ wasAnswered: true });
    if (!hungUp) {
      log.warn('Failed to hang up.', { status: this.status });
      message.warn('Failed to pause session. Please try again.');
      this.setStatus(status);
    } else if (this.status !== OutboundSessionStates.paused) {
      log.warn('State changed while hanging up.', { was: status, is: this.status });
      message.warn('Failed to pause session. Please try again.');
    }
  }

  async resume() {
    log.debug('Resuming session.');
    if (this.status !== OutboundSessionStates.paused) {
      log.error('resume() called on non-paused session.', { status: this.status });
      return;
    }
    this.setStatus(OutboundSessionStates.dialing);
  }

  async stop(force = false) {
    if (!this.isOpen() && !force) return;
    log.debug('Stopping DialSession.');
    this.cancelTimer();
    await this.hangUp();
    this.setStatus(OutboundSessionStates.closed);
    this.eventSource?.close();
    this.eventSource = null;
    this.taskQueue = [];
    await this.twilioInstance!.destroy();
    try {
      log.debug('Closing dial session.', { sessionId: this.sessionId });
      await postCloseDialSession(this.sessionId!);
      log.debug('Dial session closed.', { sessionId: this.sessionId });
    } catch (error) {
      log.error('Failed to close dial session.', { error: formatError(error) });
    }
  }

  markSkipped(task: CallTask, skipped: boolean) {
    log.debug('Attempt to mark task as skipped.', { task, skipped });
    this.handleMessage({
      ...task,
      status: skipped ? CallTaskStatus.SKIPPED : CallTaskStatus.PENDING,
    });
  }

  async markNumberInvalid(task: CallTask, number: string) {
    log.debug('Attempt to mark number as invalid.', { task, number });
    const localTask = this.taskQueue.find(t => t.frontendId === task.frontendId);
    if (!localTask) {
      log.warn('Task not found in queue.', { task });
      return;
    }
    const invalidNumbers = [number, ...(localTask.invalidNumbers ?? [])];
    const updatedTask = { ...localTask, invalidNumbers };
    this.handleMessage(updatedTask);
    if (localTask.status === CallTaskStatus.ANSWERED) {
      await this.hangOneUp(updatedTask);
    }
    await deleteLbbContactPhoneNumber(updatedTask.contact.id, number);
    if (this.sessionId && updatedTask.callSid) {
      await postMarkDialTaskNumberInvalid(this.sessionId, updatedTask.callSid, number);
    }
    if (taskHasPendingNumbers(updatedTask)) {
      this.handleMessage({ ...updatedTask, status: CallTaskStatus.PENDING }, true);
    }
  }

  async callNextNumber(task: CallTask) {
    log.debug('Attempt to call next number.', { task });
    let localTask = this.taskQueue.find(
      t => t.frontendId === task.frontendId,
    ) as DialTask;
    if (!localTask) {
      log.warn('Task not found in queue.', { task });
      return;
    }
    if (
      !(
        this.status === OutboundSessionStates.paused ||
        (this.status === OutboundSessionStates.connected &&
          localTask.status === CallTaskStatus.ANSWERED)
      )
    ) {
      log.warn('callNextNumber: session is not paused or is connected to another task', {
        status: this.status,
        task: localTask,
      });
      return;
    }
    if (this.status === OutboundSessionStates.connected) {
      await this.hangUp();
    }
    if (this.status !== OutboundSessionStates.paused) {
      log.warn('callNextNumber: session is not paused', { status: this.status });
      return;
    }
    // Reload the task in case it changed in the mean time (for
    // example, if we had to hang up).
    localTask = this.taskQueue.find(t => t.frontendId === task.frontendId) as DialTask;
    const attemptedNumbers = localTask.attemptedNumbers ?? [];
    const pendingNumbers = getTaskPendingNumbers(localTask);
    if (pendingNumbers.length < 1) {
      log.warn("callNextNumber: task doesn't have any pending numbers", { localTask });
      return;
    }
    const updatedTask = {
      ...localTask,
      status: CallTaskStatus.PENDING,
      number: pendingNumbers[0].number,
      attemptedNumbers: Array.from(
        new Set([
          localTask.number as string, // guaranteed to have a number at this point
          ...attemptedNumbers,
        ]),
      ),
    };
    // HACK: Ensure the task is at the front of the queue so that when
    // we resume, it's picked back up and called.
    this.taskQueue = [
      (updatedTask as unknown) as CallTask,
      ...this.taskQueue.filter(t => t.frontendId !== task.frontendId),
    ];
    await this.resume();
  }

  muteIncomingAudio(muted: boolean) {
    log.assert(this.isOpen(), 'mute() called on closed session');
    this.twilioInstance?.muteIncomingAudio(muted);
  }

  muteOutgoingAudio(muted: boolean) {
    this.twilioInstance?.muteOutgoingAudio(muted);
  }

  setInputDevice(device: string) {
    log.assert(this.isOpen(), 'setInputDevice() called on closed session');
    this.twilioInstance?.setInputDevice(device ?? 'default');
  }

  isOpen(): boolean {
    return (
      this.status !== OutboundSessionStates.closed &&
      this.status !== OutboundSessionStates.initializing
    );
  }

  // Private Methods
  // ===============
  // Roughly in order of appearance.

  private setStatus(status: OutboundSessionStates, ...args: (DialTask | CallTask)[]) {
    log.debug('Changing status.', { was: this.status, is: status });
    this.status = status;
    this.emit('session.status', status);
    switch (status) {
      case OutboundSessionStates.acceptingCall:
        this.acceptDeadline = new Date().getTime() + 10 * 1000;
        this.acceptCallId = args[0].id || null;
        return this.emit('call.accept', ...args);
      case OutboundSessionStates.awaitingUpdates:
        this.extendDeadline();
        return;
      case OutboundSessionStates.closed:
        return this.emit('session.end', this.sessionId);
      case OutboundSessionStates.connected:
        this.callStartedAt = new Date();
        return this.emit('call.connected', ...args);
      default:
        return;
    }
  }

  private async setupTwilio(sessionId: number, inputDevice: string, muted: boolean) {
    const twilio = this.twilioInstance!;
    const registered = new Promise((resolve, reject) => {
      twilio.on('device.failed', reject);
      twilio.on('device.registered', resolve);
    });
    await twilio.init();
    await registered;

    const ready = new Promise((resolve, reject) => {
      twilio.on('session.accept', resolve);
      twilio.on('session.cancel', () => this.fail('session canceled'));
      twilio.on('session.disconnect', () => this.stop());
      twilio.on('session.error', e => this.fail(e));
      twilio.on('session.reject', reject);
    });
    await twilio.connect({ sessionId: sessionId.toString() });
    await ready;

    log.debug('Setting Twilio device & mute.', { inputDevice, muted });
    twilio.setInputDevice(inputDevice);
    twilio.muteIncomingAudio(muted);
  }

  private async setupSSE(sessionId: number): Promise<void> {
    log.assert(!this.eventSource, 'eventSource already exists');
    return new Promise((resolve, reject) => {
      let open = false;
      const channel = `session.${sessionId}`;
      this.eventSource = new EventSource(`${SSE_ENDPOINT}?channel=${channel}`);
      this.eventSource.onopen = () => {
        open = true;
        resolve();
        log.debug('SSE open', channel);
      };

      this.eventSource.onerror = error => {
        if (!open) reject(error);
        log.error('SSE failed', { error: formatError(error), channel });
        const status = this.status;
        if (status !== OutboundSessionStates.reconnecting) {
          message.warn('Connection lost. Reconnecting...');
          this.setStatus(OutboundSessionStates.reconnecting);
          this.reconnectSSE(sessionId, status);
        }
      };

      this.eventSource.onmessage = ({ data }) => {
        const message = JSON.parse(data);
        if (message.heartbeat) {
          return;
        } else if (message.handshake) {
          this.handleHandshake(message);
        } else {
          this.handleMessage(message);
        }
      };
    });
  }

  // Invariant: the caller must set the state to RECONNECTING.
  private async reconnectSSE(
    sessionId: number,
    toStatus: OutboundSessionStates,
    retries = 5,
  ): Promise<void> {
    const fromStatus = this.status;
    log.debug('Reconnecting SSE.', { sessionId, fromStatus, toStatus, retries });
    if (fromStatus !== OutboundSessionStates.reconnecting) {
      log.debug('Cannot reconnect SSE.', { sessionId, fromStatus, toStatus, retries });
      return;
    }
    const retry = (error: any) => {
      if (retries > 0) {
        setTimeout(() => this.reconnectSSE(sessionId, toStatus, retries - 1), 1000);
      } else {
        this.fail(error);
      }
    };

    try {
      this.eventSource?.close();
      this.eventSource = null;
      await this.setupSSE(sessionId);
    } catch (error) {
      log.error('Failed to reconnect SSE. Retrying...', {
        sessionId,
        error: formatError(error),
      });
      return retry(error);
    }

    try {
      log.debug('Replaying in-progress tasks.', { sessionId, fromStatus, toStatus });
      const promises = [];
      for (const task of this.inProgressTasks()) {
        if (!task.id) {
          continue;
        }
        promises.push(getDialTask(task.id).then(({ data }) => data));
      }

      // Don't just update the tasks in place. Replay them through
      // handle-message to ensure the right state transitions happen.
      // Set the status to AWAITING_UPDATES first to increase the
      // likelihood that the right transitions happen.
      const tasks = await Promise.all(promises);
      this.setStatus(OutboundSessionStates.awaitingUpdates);
      for (const task of tasks) {
        this.handleMessage(task as CallTask);
      }
      this.setStatus(toStatus);
      message.success('Reconnected!');
    } catch (error) {
      log.error('Failed to refresh in-progress tasks.', {
        sessionId,
        error: formatError(error),
      });
      this.setStatus(OutboundSessionStates.reconnecting);
      return retry(error);
    }
  }

  private handleHandshake({
    dialTaskId,
    kind,
    token,
  }: {
    dialTaskId: string;
    kind: 'accept' | 'retry';
    token: string;
  }) {
    const status = this.status;
    const inProgressTasks = this.inProgressTasks();
    log.debug('handleHandshake()', {
      dialTaskId,
      kind,
      token,
      status,
      inProgressTasks,
    });
    this.extendDeadline();
    if (status !== OutboundSessionStates.awaitingUpdates) {
      log.warn('Rejecting handshake.', { dialTaskId, kind, token, status });
      postOutboundHandshakeReply(dialTaskId, token, 'hang-up');
      return;
    }
    const task = inProgressTasks.find(({ id }) => id === dialTaskId);
    if (!task) {
      log.warn('No task found for handshake.', { dialTaskId, kind, token, status });
      postOutboundHandshakeReply(dialTaskId, token, 'hang-up');
      return;
    }
    switch (kind) {
      case 'accept':
        {
          const invalidNumbers = task.invalidNumbers ?? [];
          if (invalidNumbers.includes(task.number ?? '')) {
            log.warn('Rejecting handshake for task with invalid number.', {
              dialTaskId,
              kind,
              token,
              status,
              number: task.number,
              invalidNumbers: invalidNumbers,
            });
            postOutboundHandshakeReply(dialTaskId, token, 'hang-up');
            return;
          }

          log.debug('Accepting call answer handshake.', { dialTaskId, token, status });
          this.setStatus(OutboundSessionStates.acceptingCall, task);
          postOutboundHandshakeReply(dialTaskId, token, 'ok');
        }
        break;
      case 'retry':
        {
          log.debug('Accepting call retry handshake.', { dialTaskId, token, status });
          postOutboundHandshakeReply(dialTaskId, token, 'ok');
        }
        break;
      default:
        log.warn('Unexpected handshake kind.', { kind });
        break;
    }
  }

  private handleMessage(task: CallTask, force = false) {
    const sessionId = this.sessionId;
    log.debug('handleMessage()', { sessionId, task });
    if (!this.isOpen()) {
      log.warn('Session is closed.', { task });
      return;
    }

    this.extendDeadline();
    const local = this.taskQueue.find(t => sameTask(t, task));
    if (!local) {
      log.warn('Task not in queue.', { task });
      return;
    } else if (CompletedCallTaskStates.includes(local.status) && !force) {
      log.warn('Task already marked as completed.', { task, local });
      return;
    } else if (this.status === OutboundSessionStates.reconnecting) {
      log.warn('Task update received while reconnecting.', { task, local });
      this.updateTask(task);
      return;
    }

    switch (task.status) {
      case CallTaskStatus.ANSWERED:
        log.debug('Call answered.', { task, local, was: local.status });
        switch (this.status) {
          case OutboundSessionStates.acceptingCall:
            if (task.id !== this.acceptCallId) {
              // This should never happen, but just in case.
              log.warn('Received answer from unexpected call task.', { task, local });
              this.hangOneUp(task, true);
              return;
            }
            this.hangUp({ except: [task.frontendId] });
            this.setStatus(OutboundSessionStates.connected, task);
            this.updateTask(task);
            break;
          default:
            if (local.status !== CallTaskStatus.ANSWERED) {
              // Intentionally doesn't update the task to avoid confusing the user.
              log.warn('Unexpected call answer.', { task, local, was: local.status });
              this.hangOneUp(task, true);
              return;
            }
        }
        break;
      case CallTaskStatus.BAD_NUMBER:
      case CallTaskStatus.BUSY:
      case CallTaskStatus.CANCELED:
      case CallTaskStatus.COMPLETED:
      case CallTaskStatus.ERROR:
      case CallTaskStatus.FAILED:
      case CallTaskStatus.LEFT_VOICEMAIL:
      case CallTaskStatus.NO_ANSWER:
      case CallTaskStatus.NO_NUMBER:
        log.debug('Call completed.', { task, local, was: local.status, is: task.status });
        this.updateTask(task);
        switch (this.status) {
          case OutboundSessionStates.awaitingUpdates:
            this.setStatus(OutboundSessionStates.dialing);
            break;
          case OutboundSessionStates.connected:
            switch (local.status) {
              case CallTaskStatus.ANSWERED:
                this.callStartedAt = null;
                this.setStatus(OutboundSessionStates.paused);
                if (this.stopOnCallCompleted) {
                  this.stop();
                }
                break;
            }
            break;
        }
        break;
      default:
        log.debug('Call updated.', { task, local, was: local.status, is: task.status });
        this.updateTask(task);
        break;
    }
  }

  private extendDeadline() {
    const now = new Date().getTime();
    this.awaitDeadline = Math.min(
      (this.awaitDeadline ?? now) + 30 * 1000,
      now + 60 * 1000,
    );
  }

  private startTimer() {
    log.assert(this.timer === null, 'timer already started');
    log.debug('Starting timer...', { sessionId: this.sessionId });
    this.timer = setInterval(() => {
      if (this.shouldProcessQueue()) {
        this.processQueue();
      } else {
        this.fail('Too many failed tasks');
      }
    }, this.queueTick);
  }

  private cancelTimer() {
    log.assert(this.timer !== null, 'timer not started');
    log.debug('Stopping timer...', { sessionId: this.sessionId });
    clearInterval(this.timer!);
    this.timer = null;
  }

  private fail(error: any) {
    log.error('Failed.', { error: formatError(error) });
    message.error(`Dial session failed: ${formatError(error)}`);
    this.stop(true);
  }

  private pendingTasks() {
    return this.taskQueue.filter(t =>
      [CallTaskStatus.PENDING, CallTaskStatus.RETRY].includes(t.status),
    );
  }

  private inProgressTasks() {
    return this.taskQueue.filter(t =>
      InProgressCallTaskStates.includes(t.status),
    ) as DialTask[];
  }

  private shouldProcessQueue(): boolean {
    return this.getAllFailedTaskCount() < 10;
  }

  // This task _MUST NOT_ be async. It gets called by our ticker, so
  // it has a finite amount of time that it needs to finish its work
  // in. When it needs to do asynchronous work, it needs to change the
  // status to AWAITING_UPDATES or similar, so that other ticks don't
  // duplicate its work, then restore the status when those tasks are
  // done if necessary.
  private processQueue() {
    const inProgressTasks = this.inProgressTasks();
    const pendingTasks = this.pendingTasks();
    const tasksToStart = this.parallelCallCount - inProgressTasks.length;
    const sessionId = this.sessionId!;

    const time = new Date().getTime();
    log.debug(`processQueue() ${this.status}`, {
      sessionId,
      inProgressTasks,
      pendingTasks,
      tasksToStart,
    });
    switch (this.status) {
      case OutboundSessionStates.acceptingCall:
        {
          if (
            this.acceptCallId !== null &&
            this.acceptDeadline !== null &&
            time > this.acceptDeadline
          ) {
            log.warn('Call accept deadline exceeded.');
            this.acceptDeadline = null;
            this.acceptCallId = null;
            this.hangUp().finally(() => {
              this.setStatus(OutboundSessionStates.dialing);
            });
          }
        }
        break;
      case OutboundSessionStates.awaitingUpdates:
        if (this.awaitDeadline !== null && time >= this.awaitDeadline) {
          log.warn('Await deadline exceeded.');
          this.awaitDeadline = null;
          this.hangUp().finally(() => {
            this.setStatus(OutboundSessionStates.dialing);
          });
        }
        break;
      case OutboundSessionStates.connected:
        if (!inProgressTasks.length) {
          log.warn('State is connected, but have no in-progress tasks.');
          this.setStatus(OutboundSessionStates.dialing);
        }
        break;
      case OutboundSessionStates.dialing:
        if (!inProgressTasks.length && !pendingTasks.length) {
          if (this.loadMoreData) {
            this.setStatus(OutboundSessionStates.loading);
            this.loadMoreData()
              .then(({ tasks, next }) => {
                if (this.status !== OutboundSessionStates.loading) {
                  log.warn('Moved out of loading state.', { sessionId, tasks });
                } else if (tasks.length) {
                  log.debug('Received more tasks.', { sessionId, tasks });
                  this.taskQueue = tasks;
                  this.loadMoreData = next;
                  this.setStatus(OutboundSessionStates.dialing);
                } else {
                  log.debug('Task source exhausted.', { sessionId });
                  this.loadMoreData = null;
                  this.stop();
                }
              })
              .catch(error => {
                log.error('Failed to load more data.', {
                  sessionId,
                  error: formatError(error),
                });
                this.loadMoreData = null;
                this.stop();
              });
            return;
          } else {
            log.debug('Queue exhausted.', { sessionId });
            this.stop();
            return;
          }
        }

        this.setStatus(OutboundSessionStates.awaitingUpdates);
        for (let i = 0; i < tasksToStart && i < pendingTasks.length; i++) {
          const callTask = pendingTasks[i];
          log.debug('Launching task.', { task: callTask });
          postDialTask(sessionId, {
            callTask,
            selectedNumber: this.selectedNumber!,
            sourceType: this.sourceType!,
            amdMode: this.amdMode,
          })
            .then(({ data }) => {
              log.debug('Launched task.', { task: data });
              if (data.shouldRecord) {
                transcribe(this.twilioInstance!, data.id);
              }
              this.handleMessage({ ...callTask, ...data } as CallTask);
            })
            .catch(error => {
              log.error('Failed to launch task.', {
                task: callTask,
                error: formatError(error),
              });
              if (this.status === OutboundSessionStates.awaitingUpdates) {
                this.setStatus(OutboundSessionStates.dialing);
              }

              const failedTaskCount =
                (this.failedTaskCounts[callTask.frontendId] ?? 0) + 1;
              this.failedTaskCounts[callTask.frontendId] = failedTaskCount;
              let status: CallTaskStatus;
              if (failedTaskCount === 3) {
                status = CallTaskStatus.ERROR;
              } else {
                status = CallTaskStatus.RETRY;
              }
              this.handleMessage({ ...callTask, status });
            });
        }
        return;
      default:
        return;
    }
  }

  private setMachineDetection(
    amdState: OutboundAMDState,
    preRecordedVoicemailId?: number | null,
  ) {
    log.debug('Changing AMD state.', { amdState });

    const state = preRecordedVoicemailId ? OutboundAMDState.ON : amdState;

    switch (state) {
      case OutboundAMDState.OFF:
        this.amdMode = 'OFF';
        break;
      case OutboundAMDState.AUTO:
      case OutboundAMDState.ON:
        this.amdMode =
          this.parallelCallCount > 1 || !!preRecordedVoicemailId ? 'SLOW' : 'FAST';
        break;
    }
  }

  // Invariant: MUST ONLY be called by either handleMessage or processQueue.
  private mapTask(task: CallTask, proc: (t: CallTask) => CallTask) {
    this.taskQueue = this.taskQueue.map(t => {
      if (!sameTask(t, task)) return t;
      const oldTask = cloneDeep(t);
      const updatedTask = proc(t);
      this.emit('task.changed', updatedTask, oldTask);
      return updatedTask;
    });
  }

  // Invariant: MUST ONLY be called by either handleMessage or processQueue.
  private updateTask(task: CallTask) {
    this.mapTask(task, t => ({
      ...t,
      ...task,
      contact: {
        ...t.contact,
      },
    }));
  }

  private async hangOneUp(
    task: CallTask,
    canRetry = false,
    wasAnswered = false,
  ): Promise<boolean> {
    if (!this.isOpen()) {
      log.error('hangOneUp() called on closed session.', { status: this.status });
      return false;
    }
    if (!task.callSid) {
      log.debug('Task has no call SID.', { task });
      return false;
    }
    log.debug('Closing task.', { task });
    const completed = wasAnswered || task.status === CallTaskStatus.ANSWERED;
    canRetry = canRetry && task.contact?.phoneNumbers?.length > 1;
    const status = completed
      ? CallTaskStatus.COMPLETED
      : canRetry
      ? CallTaskStatus.RETRY
      : CallTaskStatus.CANCELED;
    const { data } =
      status === CallTaskStatus.RETRY
        ? await postRetryDialTask(this.sessionId!, task.callSid!, this.amdMode)
        : await postCloseDialTask(this.sessionId!, task.callSid!, completed);
    this.handleMessage(data as CallTask);
    log.debug('Task closed.', { task: data });
    return true;
  }

  private getAllFailedTaskCount(): number {
    return Object.values(this.failedTaskCounts).reduce((acc, cur) => acc + cur, 0);
  }
}

export function sameTask(a: CallTask | DialTask, b: CallTask | DialTask): boolean {
  return a.frontendId === b.frontendId;
}

function taskHasPendingNumbers(t: CallTask | DialTask): boolean {
  return getTaskPendingNumbers(t).length > 0;
}

export function formatError(error: any): string {
  if (typeof error === 'string') {
    return error;
  } else if (error.message !== undefined) {
    return error.message;
  } else {
    return error.toString();
  }
}
